import { useContext } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import useCRUD from '../useCRUD';
import { QUERY_CACHE_TIME } from '../../constants';
import { mappedArray } from '../../utils/array';
import useToaster from '../useToaster';
import useAccount from './useAccount';
import useContractAccess from './useContractAccess';

const useLeaveContracts = () => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { hasContractAccess } = useContractAccess();
  const queryClient = useQueryClient();
  const { addResponseError, addSuccess } = useToaster();

  const storeKey = ['leaveContracts', accountId, hasContractAccess];

  const { isLoading, data } = useQuery(storeKey, async () => api.fetchLeaveContracts({ accountId }), {
    enabled: !!accountId && hasContractAccess,
    cacheTime: QUERY_CACHE_TIME
  });

  const leaveContracts = data?.data || [];
  const mappedLeaveContracts = mappedArray(leaveContracts);

  // Also invalidate useContractLeaves so reopening picks up the edited leaves.
  const invalidateContractLeaves = (contractId) => {
    if (!contractId) return;
    queryClient.invalidateQueries(['contractLeaves', accountId, contractId]);
  };

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: (contract) => api.addLeaveContract({ accountId, ...contract }),
      editApi: (contract) => api.updateLeaveContract({ accountId, leaveContractId: contract.id, ...contract }),
      deleteApi: async ({ id }) => {
        try {
          const result = await api.deleteLeaveContract({ accountId, leaveContractId: id });
          if (result) {
            addSuccess(t('leaveContractDeletedSuccessfully'));
            invalidateContractLeaves(id);
          }
          return result;
        } catch (e) {
          addResponseError(e);
        }
      }
    },
    { onSuccess: (variables) => invalidateContractLeaves(variables?.id) }
  );

  return {
    isLoading,
    leaveContracts,
    mappedLeaveContracts,
    addLeaveContract: addItem,
    editLeaveContract: editItem,
    deleteLeaveContract: deleteItem
  };
};

export default useLeaveContracts;
